import { z } from "zod/v4";
import {
  vizExportFormatSchema,
  vizSortDirectionSchema,
} from "@/lib/ai/visualization/views/shared/schema-primitives";

const tableExportFormatsSchema = z
  .array(vizExportFormatSchema)
  .max(3)
  .optional()
  .nullable();

const vizColumnLabelMapSchema = z
  .object({})
  .catchall(z.string().min(1))
  .optional()
  .nullable();

const vizColumnPinningSchema = z
  .object({
    left: z.array(z.string()).max(2).optional().nullable(),
    right: z.array(z.string()).max(2).optional().nullable(),
  })
  .strict()
  .optional()
  .nullable();

const vizRowPinningSchema = z
  .object({
    top: z.array(z.number().int().min(0)).max(3).optional().nullable(),
    bottom: z.array(z.number().int().min(0)).max(3).optional().nullable(),
  })
  .strict()
  .optional()
  .nullable();

const vizTableColumnSizingSchema = z
  .object({})
  .catchall(
    z
      .object({
        size: z.number().int().min(40).max(1200).optional().nullable(),
        minSize: z.number().int().min(40).max(1200).optional().nullable(),
        maxSize: z.number().int().min(40).max(1200).optional().nullable(),
      })
      .strict()
      .nullable(),
  )
  .optional()
  .nullable();

/**
 * `table` authoring contract。
 *
 * @remarks
 * provider-facing schema では paired-field 補正を持ち込まず、`sort` を 1 つの
 * strict object に畳んで structured output の branch ambiguity を減らす。
 */
const tableAuthoringSortSchema = z
  .object({
    key: z.string().min(1),
    direction: z.enum(["asc", "desc"]),
  })
  .strict();

export const tableAuthoringSchema = z
  .object({
    columns: z.array(z.string().min(1)),
    sort: tableAuthoringSortSchema.nullable(),
    limit: z.number().int().min(1).nullable(),
  })
  .strict();

const nullableTableBooleanSchema = z.boolean().optional().nullable();

/**
 * `VizTable` runtime component props schema。
 */
export const tableComponentSchema = z
  .object({
    dataPath: z.string(),
    columns: z.array(z.string()).optional().nullable(),
    limit: z.number().int().min(1).optional().nullable(),
    columnLabels: vizColumnLabelMapSchema,
    defaultSort: z
      .object({
        key: z.string(),
        direction: vizSortDirectionSchema,
      })
      .strict()
      .optional()
      .nullable(),
    allowColumnVisibilityToggle: nullableTableBooleanSchema,
    defaultVisibleColumns: z.array(z.string()).optional().nullable(),
    allowExport: nullableTableBooleanSchema,
    exportFormats: tableExportFormatsSchema,
    enableMultiSort: nullableTableBooleanSchema,
    columnPinning: vizColumnPinningSchema,
    rowPinning: vizRowPinningSchema,
    columnSizing: vizTableColumnSizingSchema,
  })
  .strict();
